import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { DriverDetailsResponse, DriverManagementService, DriverManagementVerbs } from '@cordic/driver-management';
import { TranslateService } from '@ngx-translate/core';
import { Observable, TimeoutError } from 'rxjs';
import { User } from 'src/app/models/interfaces/user.model';
import { LoginService } from 'src/app/services/login-service/login-service.service';
import { AppRoutes } from 'src/app/app-routes';
import { VariableTypeEnum } from 'src/app/models/enum/variable-type.enum';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ErrorModalComponent } from 'src/app/components/error-modal/error-modal.component';
import { NgxSpinnerService } from 'ngx-spinner';
import { RequestOptionsProviderService } from 'src/app/services/request-options-provider/request-options-provider.service';

enum LoginType {
  PHONE = 0,
  EMAIL
}

enum TypeString {
  PHONE = 'Phone Number',
  PHONE_SHORT = 'tel',
  EMAIL = 'Email',
  EMAIL_SHORT = 'email',
  PASSWORD = 'Password'
}

export enum DriverStatusCode {
  UNKNOWN = 0,
  REGISTERED,
  ACTIVE,
  LOCKED,
  BLACKLISTED
}

enum loginErrorType {
  USER_LOCKED = 8,
  LOGIN_FAIL = 40,
  NO_LOGIN_ID = 42,
  LOGIN_ID_INVALID = 43,
  INVALID_PASSWORD = 44,
}


@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
  constructor (
    private translate: TranslateService, 
    private loginService: LoginService, 
    private router: Router,
    private reqOpt: RequestOptionsProviderService,
    private spinner: NgxSpinnerService,
    private driverManagementService: DriverManagementService, 
    public modalService: NgbModal) 
    { }

  loginType = LoginType.PHONE;
  user: User | undefined;
  showPasswordField: boolean = true;

  AppRoutes = AppRoutes;

  userLoginForm: FormGroup<{user:FormControl<any>; password:FormControl<any>; rememberPwd: FormControl<any>}> = new FormGroup({
    user: new FormControl(null, Validators.required),
    password: new FormControl(null, [Validators.required, Validators.minLength(6)]),
    rememberPwd: new FormControl(null)
  });

  ngOnInit(): void
  {
    this.configureValidators();

    if (this.reqOpt.jwt)
    {
      this.reqOpt.jwt = undefined;
    }
  }

  getLoginType(capitalise: boolean): string
  {
    if (this.loginType === 0)
    {
      return capitalise ? TypeString.PHONE : TypeString.PHONE_SHORT;
    }
    else
    {
      return capitalise ? TypeString.EMAIL : TypeString.EMAIL_SHORT;
    }
  }

  getPasswordPlaceholder(): string
  {
    return TypeString.PASSWORD;
  }

  configureValidators()
  {
    this.userLoginForm.controls.user.reset();
    if (this.user)
    {
      this.userLoginForm.controls.user.disable();
    }
    else
    {
      this.userLoginForm.controls.user.enable();
    }

    if (this.showPasswordField)
    {
      this.userLoginForm.controls.password.enable();
    }
    else
    {
      this.userLoginForm.controls.password.disable();
    }

    if (this.loginType === LoginType.EMAIL)
    {
      this.userLoginForm.controls.user.setValidators([
        Validators.email,
        Validators.required,
      ]);
    }
    else if (this.loginType === LoginType.PHONE)
    {
      this.userLoginForm.controls.user.setValidators([
        Validators.required, Validators.pattern('(\\s*[0-9]){5,}\\s*'),
      ]);
    }

    this.userLoginForm.updateValueAndValidity();
  }

  getFormattedFormUser()
  {
    let formUser = this.userLoginForm.value.user;

    if (( typeof formUser === VariableTypeEnum.STRING ) && (this.userLoginForm))
    {
      // remove all whitespaces
      formUser = formUser.replace(/\s/g, '');
      this.userLoginForm.controls.user.setValue(formUser);
    }

    return formUser;
  }

  async onLogin()
  {
    this.spinner.show();

    const user = this.getFormattedFormUser();

    !this.userLoginForm.invalid ? 
    await this.loginService.login(user, this.userLoginForm.controls.password.value)
    .then(() => {
      this.spinner.hide();

      this.router.navigate([AppRoutes.Details]);
    })
    .then(() => this.userLoginForm.reset())
    .catch(error => {
      if (error instanceof TimeoutError)
      {
        const modalRef = this.modalService.open(ErrorModalComponent, { backdrop: 'static', centered: true });

        modalRef.componentInstance.errText = this.translate.instant("IDS_ERR_SERVER_TIMEOUT");

        this.spinner.hide();
      }
      else
      {
        let errorMsg = '';

        switch(error.code)
        {
          case loginErrorType.USER_LOCKED:
            errorMsg = this.translate.instant('IDS_LOGIN_ERROR_MSG', {errorType: this.translate.instant('IDS_LOGIN_ERROR_USER_LOCKED')});
            break;
          case loginErrorType.LOGIN_FAIL:
            errorMsg = this.translate.instant('IDS_LOGIN_ERROR_MSG', {errorType: this.translate.instant('IDS_LOGIN_ERROR_LOGIN_FAIL')});
            break;
          case loginErrorType.NO_LOGIN_ID:
          case loginErrorType.LOGIN_ID_INVALID:
          case loginErrorType.INVALID_PASSWORD:
            errorMsg = this.translate.instant('IDS_LOGIN_ERROR_MSG', {errorType: this.translate.instant('IDS_LOGIN_ERROR_INVALID_LOGIN')});
            break;
        }

        const modalRef = this.modalService.open(ErrorModalComponent, {backdrop: 'static', centered: true });

        modalRef.componentInstance.errText = errorMsg;

        this.spinner.hide();

        throw error;
      }
    })
    : null
  }

  driverManagementDetails(driverUserID: number) : Observable<DriverDetailsResponse>
  {
    return this.driverManagementService.post(DriverManagementVerbs.DETAILS, { driverUserID });
  }

  shouldAllowLogin(): boolean
  {
    return this.userLoginForm.invalid;
  }
}
